/**
 * Yield & ROI tab — Phase 2C-i.5 (Option A scope: Sections A · B · C · D · F).
 * Section E (critical-path SVG timeline) + live telemetry widgets ship in 2C-i.5b.
 *
 * Guardrails honoured:
 *  G1 — direct (formwork + labor) and proxy (overhead/LD) reported separately.
 *  G2 — every record carries its provenance (monitored estimate · maturity-
 *       verified achievement · contractual baseline · timestamp).
 *  G3 — copy frames yield as a layer ON TOP OF thermal + maturity, not a
 *       replacement; footer micro-caption restates the split.
 *  G5 — currency defaults from `useRegion()` for accounts with no settings yet.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, Plus, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";

import { TierGate } from "@/components/tier/TierGate";
import { authService, formatApiError } from "@/services/authService";
import { useRegion } from "@/contexts/RegionContext";

/* ===================== constants ===================== */

const CURRENCIES = [
    { code: "USD", symbol: "$" },
    { code: "GBP", symbol: "£" },
    { code: "EUR", symbol: "€" },
    { code: "HKD", symbol: "HK$" },
    { code: "AUD", symbol: "A$" },
];

const MILESTONE_TYPES = [
    { value: "formwork_strike",   label: "Formwork strike",   defaultHours: 15 },
    { value: "falsework_removal", label: "Falsework removal", defaultHours: 20 },
    { value: "pt_stressing",      label: "PT stressing",      defaultHours: 21 },
    { value: "custom",            label: "Custom milestone",  defaultHours: 24 },
];

const fmtNumber = (n) =>
    Number(n || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0, maximumFractionDigits: 2,
    });

const currencyPrefix = (code) =>
    (CURRENCIES.find((c) => c.code === code) || {}).symbol || code;

/* ===================== service ===================== */

const yieldApi = {
    getSettings:  (pid)               => authService.http.get(`/projects/${pid}/yield_settings`),
    putSettings:  (pid, body)         => authService.http.put(`/projects/${pid}/yield_settings`, body),
    listPours:    (pid)               => authService.http.get(`/projects/${pid}/pours`),
    listRecords:  (pid, q = "")       => authService.http.get(`/projects/${pid}/yield_records${q}`),
    listForPour:  (pourId)            => authService.http.get(`/pours/${pourId}/yield_records`),
    compute:      (pourId)            => authService.http.post(`/pours/${pourId}/yield_records/compute`),
    deleteRecord: (id)                => authService.http.delete(`/yield_records/${id}`),
};

/* ===================== main ===================== */

export default function YieldTab() {
    return (
        <TierGate min="professional" featureLabel="Yield & ROI">
            <YieldTabInner />
        </TierGate>
    );
}

function YieldTabInner() {
    const { project } = useOutletContext() || {};
    const { currency: regionCurrency } = useRegion();
    const projectId = project?.id;

    const [settings, setSettings] = useState(null);
    const [pours, setPours] = useState([]);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState("all");

    const refresh = useCallback(async () => {
        if (!projectId) return;
        try {
            const [s, p, r] = await Promise.all([
                yieldApi.getSettings(projectId),
                yieldApi.listPours(projectId),
                yieldApi.listRecords(projectId),
            ]);
            setSettings(s.data);
            setPours(p.data || []);
            setRecords(r.data || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Could not load Yield data.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (loading || !settings) {
        return (
            <div className="p-12 flex items-center gap-3 text-[#5a6669] tc-mono text-[12px]">
                <Loader2 size={14} className="animate-spin" /> Loading Yield & ROI…
            </div>
        );
    }

    // Filtering
    const filtered = filterType === "all"
        ? records
        : records.filter((r) => r.milestone_type === filterType);

    const totals = filtered.reduce(
        (acc, r) => {
            acc.tDelta += r.t_delta_hours || 0;
            acc.direct += r.direct_savings || 0;
            acc.proxy  += r.proxy_savings  || 0;
            acc.total  += r.total_savings  || 0;
            return acc;
        },
        { tDelta: 0, direct: 0, proxy: 0, total: 0 },
    );

    const cur = settings.currency || regionCurrency || "USD";
    const sym = currencyPrefix(cur);

    return (
        <div data-testid="yield-tab" className="flex flex-col gap-12">
            <Header projectName={project?.name} regionCurrency={regionCurrency} settingsCurrency={cur} settingsId={settings.id} />

            <SectionA
                totals={totals}
                currency={cur}
                symbol={sym}
                filterType={filterType}
                setFilterType={setFilterType}
                milestoneTypes={
                    Array.from(new Set(records.map((r) => r.milestone_type)))
                }
            />

            <SectionB
                settings={settings}
                projectId={projectId}
                onSaved={(s) => setSettings(s)}
            />

            <SectionC
                settings={settings}
                projectId={projectId}
                onSaved={(s) => setSettings(s)}
            />

            <SectionD
                pours={pours}
                records={records}
                settings={settings}
                onComputed={refresh}
                projectId={projectId}
            />

            <SectionE pours={pours} records={records} />

            {/* G3 + footer micro-caption */}
            <p
                data-testid="yield-footer-caption"
                className="tc-mono text-[10.5px] tracking-wider text-[#5a6669] border-t border-[#d8dcdd] pt-5 leading-[1.6]"
            >
                Yield Engine v0.1 · figures denominated in {cur} · direct
                savings: formwork + labor · proxy savings: overhead / LD
                assumption. Yield sits on top of the thermal and maturity
                cores — temperature → equivalent age → calibrated strength →
                milestone → yield.
            </p>
        </div>
    );
}

/* ===================== header (G3 framing) ===================== */

const Header = ({ projectName, regionCurrency, settingsCurrency, settingsId }) => (
    <div className="flex flex-col gap-2">
        <span className="tc-eyebrow text-[#01696f]">Yield & ROI · Pro</span>
        <h2 className="text-[22px] font-medium tracking-tight text-[#0e1416]">
            Operational yield from maturity-verified milestones
        </h2>
        <p className="text-[13.5px] text-[#5a6669] leading-[1.65] max-w-[78ch]">
            An operational decision layer built on the engineering core.
            Yield reads from monitored strength estimates: temperature →
            equivalent age → calibrated strength → milestone → yield. It does
            not replace the thermal/cracking and maturity/strength outputs —
            it converts them to schedule-compression and cost figures.
        </p>
        {!settingsId && (
            <p
                data-testid="yield-region-default-note"
                className="mt-2 tc-mono text-[11px] text-[#8a9498]"
            >
                Default currency for this project: {settingsCurrency} —
                inferred from your account region ({regionCurrency}). Override
                in Commercial assumptions below.
            </p>
        )}
    </div>
);

/* ===================== Section A — cumulative ===================== */

const SectionA = ({ totals, currency, symbol, filterType, setFilterType, milestoneTypes }) => (
    <section data-testid="yield-section-a-cumulative" className="flex flex-col gap-5">
        <SectionLabel index="A" title="Cumulative project yield" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#d8dcdd] border border-[#d8dcdd]">
            <Stat
                label="Schedule reclaimed"
                value={`${fmtNumber(totals.tDelta)} h`}
                sub={`${fmtNumber(totals.tDelta / 24)} days`}
                testid="yield-stat-schedule"
            />
            <Stat
                label="Direct savings"
                value={`${symbol} ${fmtNumber(totals.direct)}`}
                sub={`${currency} · formwork + labor`}
                emphasis
                testid="yield-stat-direct"
            />
            <Stat
                label="Proxy savings"
                value={`${symbol} ${fmtNumber(totals.proxy)}`}
                sub={`${currency} · overhead / LD (proxy)`}
                muted
                testid="yield-stat-proxy"
            />
            <Stat
                label="Total potential yield"
                value={`${symbol} ${fmtNumber(totals.total)}`}
                sub="direct + proxy"
                emphasis
                testid="yield-stat-total"
            />
        </div>

        {milestoneTypes.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
                <span className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669] mr-1">
                    Filter
                </span>
                <FilterChip active={filterType === "all"} onClick={() => setFilterType("all")} label="All milestones" testid="yield-filter-all" />
                {milestoneTypes.map((mt) => (
                    <FilterChip
                        key={mt}
                        active={filterType === mt}
                        onClick={() => setFilterType(mt)}
                        label={MILESTONE_TYPES.find((m) => m.value === mt)?.label || mt}
                        testid={`yield-filter-${mt}`}
                    />
                ))}
            </div>
        )}
    </section>
);

const Stat = ({ label, value, sub, emphasis, muted, testid }) => (
    <div
        data-testid={testid}
        className="bg-[#ffffff] px-5 py-5 flex flex-col gap-1.5"
    >
        <span className="tc-mono text-[10px] tracking-widest uppercase text-[#5a6669]">{label}</span>
        <span className={`tc-mono ${emphasis ? "text-[22px] text-[#0e1416] font-medium" : muted ? "text-[16px] text-[#5a6669]" : "text-[18px] text-[#0e1416]"} tracking-tight`}>
            {value}
        </span>
        {sub && <span className="tc-mono text-[10.5px] text-[#8a9498]">{sub}</span>}
    </div>
);

const FilterChip = ({ active, onClick, label, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`tc-mono text-[11px] px-2.5 py-1 border transition-colors ${
            active
                ? "border-[#01696f] text-[#01696f] bg-[rgba(1,105,111,0.06)]"
                : "border-[#d8dcdd] text-[#5a6669] hover:border-[#01696f] hover:text-[#01696f]"
        }`}
    >
        {label}
    </button>
);

/* ===================== Section B — commercial assumptions ===================== */

const SectionB = ({ settings, projectId, onSaved }) => {
    const [rates, setRates] = useState({
        formwork_lease_rate_per_day: settings.formwork_lease_rate_per_day,
        labor_burn_rate_per_hour:    settings.labor_burn_rate_per_hour,
        overhead_proxy_per_day:      settings.overhead_proxy_per_day,
    });
    const [overheadOn, setOverheadOn] = useState(settings.enable_overhead_proxy);
    const [confirm, setConfirm] = useState(null); // { from, to, suggested_defaults }
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setRates({
            formwork_lease_rate_per_day: settings.formwork_lease_rate_per_day,
            labor_burn_rate_per_hour:    settings.labor_burn_rate_per_hour,
            overhead_proxy_per_day:      settings.overhead_proxy_per_day,
        });
        setOverheadOn(settings.enable_overhead_proxy);
    }, [settings]);

    const save = useCallback(async (overrides = {}) => {
        setSaving(true);
        try {
            const body = {
                currency: overrides.currency || settings.currency,
                ...rates,
                ...overrides,
                enable_overhead_proxy: overheadOn,
                milestones: settings.milestones || [],
            };
            const { data } = await yieldApi.putSettings(projectId, body);
            onSaved(data);
            toast.success("Commercial assumptions saved.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.error === "currency_change_needs_rates") {
                setConfirm({
                    from: settings.currency,
                    to: overrides.currency,
                    suggested_defaults: detail.suggested_defaults,
                });
            } else {
                toast.error(formatApiError(err) || "Could not save assumptions.");
            }
        } finally {
            setSaving(false);
        }
    }, [overheadOn, rates, settings.currency, settings.milestones, projectId, onSaved]);

    const handleCurrencyChange = (newCur) => {
        if (newCur === settings.currency) return;
        save({ currency: newCur });
    };

    return (
        <section data-testid="yield-section-b-assumptions" className="flex flex-col gap-5">
            <SectionLabel index="B" title="Commercial assumptions" />

            <div className="border border-[#d8dcdd] bg-[#ffffff] p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-3 flex flex-col gap-2">
                    <Label>Currency</Label>
                    <select
                        data-testid="yield-currency-select"
                        value={settings.currency}
                        onChange={(e) => handleCurrencyChange(e.target.value)}
                        className="tc-mono text-[13px] px-3 py-2 border border-[#d8dcdd] bg-[#ffffff] text-[#0e1416] focus:outline-none focus:border-[#01696f]"
                    >
                        {CURRENCIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>
                        ))}
                    </select>
                    <span className="tc-mono text-[10px] text-[#8a9498]">All figures denominated in {settings.currency}.</span>
                </div>

                <RateInput
                    label="Formwork lease rate"
                    suffix="/ day"
                    value={rates.formwork_lease_rate_per_day}
                    onChange={(v) => setRates({ ...rates, formwork_lease_rate_per_day: v })}
                    testid="rate-formwork"
                />
                <RateInput
                    label="Labor burn rate"
                    suffix="/ hour"
                    value={rates.labor_burn_rate_per_hour}
                    onChange={(v) => setRates({ ...rates, labor_burn_rate_per_hour: v })}
                    testid="rate-labor"
                />
                <RateInput
                    label="Overhead / LD proxy"
                    suffix="/ day"
                    value={rates.overhead_proxy_per_day}
                    onChange={(v) => setRates({ ...rates, overhead_proxy_per_day: v })}
                    testid="rate-overhead"
                />

                <div className="lg:col-span-12 flex flex-wrap items-center justify-between gap-4 border-t border-[#d8dcdd] pt-5">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="yield-overhead-toggle"
                            checked={overheadOn}
                            onChange={(e) => setOverheadOn(e.target.checked)}
                            className="accent-[#01696f]"
                        />
                        <span className="text-[13px] text-[#0e1416]">
                            Include overhead / LD proxy in yield
                        </span>
                    </label>
                    <button
                        type="button"
                        onClick={() => save()}
                        disabled={saving}
                        data-testid="yield-assumptions-save"
                        className="tc-mono text-[12px] px-4 py-2 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-50 text-[#ffffff] transition-colors"
                    >
                        {saving ? "Saving…" : "Save assumptions"}
                    </button>
                </div>
            </div>

            {/* G1 explicit copy */}
            <p
                data-testid="yield-g1-note"
                className="tc-mono text-[11px] italic text-[#5a6669] leading-[1.65] max-w-[78ch]"
            >
                Baseline estimates — override to your project&apos;s actual
                rates. Direct savings (formwork + labor) are firm cost
                reductions; the overhead / LD proxy is a time-value assumption
                and is reported separately.
            </p>

            {confirm && (
                <CurrencyConfirmDialog
                    from={confirm.from}
                    to={confirm.to}
                    suggested={confirm.suggested_defaults}
                    currentRates={rates}
                    onRestore={() => {
                        setRates(confirm.suggested_defaults);
                        save({ currency: confirm.to, ...confirm.suggested_defaults });
                        setConfirm(null);
                    }}
                    onKeep={async () => {
                        // 2C-i.5b cleanup: backend now honours force_keep_rates
                        // so we send the current rates verbatim — no mutation.
                        try {
                            setSaving(true);
                            const { data } = await yieldApi.putSettings(projectId, {
                                currency: confirm.to,
                                ...rates,
                                enable_overhead_proxy: overheadOn,
                                milestones: settings.milestones || [],
                                force_keep_rates: true,
                            });
                            onSaved(data);
                            toast.success("Currency switched. Existing rates preserved.");
                        } catch (err) {
                            toast.error(formatApiError(err) || "Could not switch currency.");
                        } finally {
                            setSaving(false);
                            setConfirm(null);
                        }
                    }}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </section>
    );
};

const Label = ({ children }) => (
    <span className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">{children}</span>
);

const RateInput = ({ label, suffix, value, onChange, testid }) => (
    <div className="lg:col-span-3 flex flex-col gap-2">
        <Label>{label}</Label>
        <div className="flex items-center border border-[#d8dcdd] focus-within:border-[#01696f] transition-colors">
            <input
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                data-testid={`yield-${testid}`}
                className="flex-1 tc-mono text-[13px] px-3 py-2 bg-transparent focus:outline-none text-[#0e1416]"
            />
            <span className="tc-mono text-[10px] text-[#5a6669] px-3 border-l border-[#d8dcdd]">{suffix}</span>
        </div>
    </div>
);

const CurrencyConfirmDialog = ({ from, to, suggested, currentRates, onRestore, onKeep, onCancel }) => (
    <div
        data-testid="yield-currency-confirm-dialog"
        className="fixed inset-0 z-50 bg-[rgba(14,20,22,0.55)] flex items-center justify-center p-6"
        onClick={onCancel}
    >
        <div
            className="bg-[#ffffff] border border-[#d8dcdd] shadow-xl max-w-[520px] w-full p-7"
            onClick={(e) => e.stopPropagation()}
        >
            <span className="tc-eyebrow text-[#01696f]">Confirm currency change</span>
            <h3 className="mt-2 text-[18px] font-medium tracking-tight text-[#0e1416]">
                Switch currency from {from} to {to}?
            </h3>
            <p className="mt-3 text-[13px] text-[#5a6669] leading-[1.6]">
                The current rates are still {from} defaults. Restore {to}
                defaults, or keep the current rates and re-edit afterwards?
            </p>
            <div className="mt-5 border border-[#d8dcdd] divide-y divide-[#d8dcdd]">
                <DialogRow k="Formwork / day"  v={`${currentRates.formwork_lease_rate_per_day} → ${suggested.formwork_lease_rate_per_day}`} />
                <DialogRow k="Labor / hour"    v={`${currentRates.labor_burn_rate_per_hour} → ${suggested.labor_burn_rate_per_hour}`} />
                <DialogRow k="Overhead / day"  v={`${currentRates.overhead_proxy_per_day} → ${suggested.overhead_proxy_per_day}`} />
            </div>
            <div className="mt-6 flex flex-wrap gap-3 justify-end">
                <button onClick={onCancel} data-testid="yield-currency-cancel"
                        className="tc-mono text-[12px] px-4 py-2 border border-[#d8dcdd] hover:border-[#0e1416] text-[#0e1416] transition-colors">
                    Cancel
                </button>
                <button onClick={onKeep} data-testid="yield-currency-keep"
                        className="tc-mono text-[12px] px-4 py-2 border border-[#01696f] text-[#01696f] hover:bg-[rgba(1,105,111,0.06)] transition-colors">
                    Keep current rates
                </button>
                <button onClick={onRestore} data-testid="yield-currency-restore"
                        className="tc-mono text-[12px] px-4 py-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] transition-colors">
                    Restore {to} defaults
                </button>
            </div>
        </div>
    </div>
);

const DialogRow = ({ k, v }) => (
    <div className="flex items-baseline justify-between px-4 py-2.5">
        <span className="tc-mono text-[11px] text-[#5a6669]">{k}</span>
        <span className="tc-mono text-[12px] text-[#0e1416]">{v}</span>
    </div>
);

/* ===================== Section C — milestones ===================== */

const SectionC = ({ settings, projectId, onSaved }) => {
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState({
        milestone_type: "formwork_strike",
        label: "Formwork strike",
        contractual_wait_duration_hours: 15,
        required_strength_MPa: 15,
    });

    const persist = async (newMilestones) => {
        setBusy(true);
        try {
            const { data } = await yieldApi.putSettings(projectId, {
                currency:                       settings.currency,
                formwork_lease_rate_per_day:    settings.formwork_lease_rate_per_day,
                labor_burn_rate_per_hour:       settings.labor_burn_rate_per_hour,
                overhead_proxy_per_day:         settings.overhead_proxy_per_day,
                enable_overhead_proxy:          settings.enable_overhead_proxy,
                milestones: newMilestones,
            });
            onSaved(data);
        } catch (err) {
            toast.error(formatApiError(err) || "Could not save milestones.");
        } finally {
            setBusy(false);
        }
    };

    const addMilestone = async () => {
        const next = [...(settings.milestones || []), draft];
        await persist(next);
        setAdding(false);
        setDraft({
            milestone_type: "formwork_strike",
            label: "Formwork strike",
            contractual_wait_duration_hours: 15,
            required_strength_MPa: 15,
        });
    };

    const removeMilestone = async (idx) => {
        const next = (settings.milestones || []).filter((_, i) => i !== idx);
        await persist(next);
    };

    return (
        <section data-testid="yield-section-c-milestones" className="flex flex-col gap-5">
            <SectionLabel index="C" title="Milestones" />

            <div className="border border-[#d8dcdd] bg-[#ffffff]">
                <div className="hidden md:grid grid-cols-12 px-5 py-3 border-b border-[#d8dcdd] bg-[#f6f7f6]">
                    <div className="col-span-4"><Label>Label</Label></div>
                    <div className="col-span-3"><Label>Type</Label></div>
                    <div className="col-span-2"><Label>Contractual (h)</Label></div>
                    <div className="col-span-2"><Label>Required f_c (MPa)</Label></div>
                    <div className="col-span-1" />
                </div>
                {(settings.milestones || []).length === 0 ? (
                    <div className="px-5 py-7 text-center text-[13px] text-[#5a6669]">
                        No milestones configured. Add formwork-strike,
                        falsework-removal, PT-stressing, or a custom milestone
                        to start computing yield.
                    </div>
                ) : (
                    (settings.milestones || []).map((m, idx) => (
                        <div
                            key={`${m.milestone_type}-${idx}`}
                            data-testid={`milestone-row-${idx}`}
                            className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-0 px-5 py-3.5 border-b border-[#d8dcdd] last:border-b-0 items-baseline"
                        >
                            <div className="md:col-span-4 text-[13.5px] text-[#0e1416]">{m.label}</div>
                            <div className="md:col-span-3 tc-mono text-[12px] text-[#5a6669]">{m.milestone_type}</div>
                            <div className="md:col-span-2 tc-mono text-[12px] text-[#0e1416]">{m.contractual_wait_duration_hours} h</div>
                            <div className="md:col-span-2 tc-mono text-[12px] text-[#0e1416]">{m.required_strength_MPa} MPa</div>
                            <div className="md:col-span-1 text-right">
                                <button
                                    onClick={() => removeMilestone(idx)}
                                    disabled={busy}
                                    data-testid={`milestone-delete-${idx}`}
                                    className="text-[#a13434] hover:text-[#7a2424] transition-colors disabled:opacity-50"
                                    title="Delete"
                                >
                                    <Trash2 size={14} strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    ))
                )}

                {adding ? (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 bg-[rgba(1,105,111,0.04)] border-t border-[#d8dcdd] items-end">
                        <div className="md:col-span-4 flex flex-col gap-1">
                            <Label>Label</Label>
                            <input
                                type="text"
                                value={draft.label}
                                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                                data-testid="milestone-draft-label"
                                className="tc-mono text-[12px] px-2.5 py-1.5 border border-[#d8dcdd] bg-[#ffffff] focus:outline-none focus:border-[#01696f]"
                            />
                        </div>
                        <div className="md:col-span-3 flex flex-col gap-1">
                            <Label>Type</Label>
                            <select
                                value={draft.milestone_type}
                                onChange={(e) => {
                                    const type = MILESTONE_TYPES.find((t) => t.value === e.target.value);
                                    setDraft({
                                        ...draft,
                                        milestone_type: e.target.value,
                                        label: type?.label || draft.label,
                                        contractual_wait_duration_hours: type?.defaultHours ?? draft.contractual_wait_duration_hours,
                                    });
                                }}
                                data-testid="milestone-draft-type"
                                className="tc-mono text-[12px] px-2.5 py-1.5 border border-[#d8dcdd] bg-[#ffffff] focus:outline-none focus:border-[#01696f]"
                            >
                                {MILESTONE_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2 flex flex-col gap-1">
                            <Label>Contractual h</Label>
                            <input
                                type="number" min="0" step="0.5"
                                value={draft.contractual_wait_duration_hours}
                                onChange={(e) => setDraft({ ...draft, contractual_wait_duration_hours: parseFloat(e.target.value) || 0 })}
                                data-testid="milestone-draft-hours"
                                className="tc-mono text-[12px] px-2.5 py-1.5 border border-[#d8dcdd] bg-[#ffffff] focus:outline-none focus:border-[#01696f]"
                            />
                        </div>
                        <div className="md:col-span-2 flex flex-col gap-1">
                            <Label>Required f_c (MPa)</Label>
                            <input
                                type="number" min="0" step="0.1"
                                value={draft.required_strength_MPa}
                                onChange={(e) => setDraft({ ...draft, required_strength_MPa: parseFloat(e.target.value) || 0 })}
                                data-testid="milestone-draft-mpa"
                                className="tc-mono text-[12px] px-2.5 py-1.5 border border-[#d8dcdd] bg-[#ffffff] focus:outline-none focus:border-[#01696f]"
                            />
                        </div>
                        <div className="md:col-span-1 flex gap-1 justify-end">
                            <button
                                onClick={addMilestone}
                                disabled={busy}
                                data-testid="milestone-save"
                                className="tc-mono text-[11px] px-3 py-1.5 bg-[#01696f] text-[#ffffff] hover:bg-[#015a60] disabled:opacity-50"
                            >Save</button>
                            <button
                                onClick={() => setAdding(false)}
                                data-testid="milestone-cancel"
                                className="tc-mono text-[11px] px-3 py-1.5 border border-[#d8dcdd] text-[#5a6669] hover:border-[#0e1416]"
                            >×</button>
                        </div>
                    </div>
                ) : (
                    <div className="border-t border-[#d8dcdd] px-5 py-3">
                        <button
                            onClick={() => setAdding(true)}
                            data-testid="milestone-add"
                            className="tc-mono text-[12px] text-[#01696f] hover:text-[#015a60] inline-flex items-center gap-1.5 transition-colors"
                        >
                            <Plus size={13} strokeWidth={1.5} />
                            Add milestone
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

/* ===================== Section D — per-pour ===================== */

const SectionD = ({ pours, records, settings, onComputed, projectId }) => {
    const noMilestones = (settings.milestones || []).length === 0;
    const sym = currencyPrefix(settings.currency);

    return (
        <section data-testid="yield-section-d-pours" className="flex flex-col gap-5">
            <SectionLabel index="D" title="Per-pour yield" />

            {pours.length === 0 ? (
                <div className="border border-[#d8dcdd] bg-[#ffffff] p-7 text-[13px] text-[#5a6669]">
                    No pours in this project yet. Add a pour in Setup, then
                    return here once monitoring data is uploaded.
                </div>
            ) : (
                <div className="flex flex-col gap-5">
                    {pours.map((pour) => {
                        const pourRecords = records.filter((r) => r.pour_id === pour.id);
                        return (
                            <PourYieldCard
                                key={pour.id}
                                projectId={projectId}
                                pour={pour}
                                records={pourRecords}
                                noMilestones={noMilestones}
                                currency={settings.currency}
                                symbol={sym}
                                onComputed={onComputed}
                            />
                        );
                    })}
                </div>
            )}
        </section>
    );
};

const PourYieldCard = ({ projectId, pour, records, noMilestones, currency, symbol, onComputed }) => {
    const [computing, setComputing] = useState(false);
    const [error, setError] = useState(null);

    const handleCompute = async () => {
        setComputing(true); setError(null);
        try {
            await yieldApi.compute(pour.id);
            toast.success(`Yield computed for Pour #${pour.sequence_index}.`);
            onComputed && onComputed();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const code = detail?.error || "compute_failed";
            setError({ code, message: detail?.message || formatApiError(err) });
        } finally {
            setComputing(false);
        }
    };

    return (
        <div
            data-testid={`pour-yield-card-${pour.id}`}
            className="border border-[#d8dcdd] bg-[#ffffff]"
        >
            <div className="flex flex-wrap items-baseline justify-between px-6 py-4 border-b border-[#d8dcdd] gap-3">
                <div className="flex items-baseline gap-3">
                    <span className="tc-mono text-[10px] tracking-widest uppercase text-[#5a6669]">
                        Pour #{pour.sequence_index}
                    </span>
                    <h4 className="text-[14.5px] font-medium tracking-tight text-[#0e1416]">
                        {pour.name || `Pour ${pour.sequence_index}`}
                    </h4>
                    <span className="tc-mono text-[10.5px] text-[#8a9498]">
                        {pour.status}
                    </span>
                </div>
                <button
                    onClick={handleCompute}
                    disabled={computing || noMilestones}
                    data-testid={`pour-yield-compute-${pour.id}`}
                    title={noMilestones ? "Configure a milestone first" : ""}
                    className="tc-mono text-[12px] px-3 py-1.5 border border-[#01696f] text-[#01696f] hover:bg-[rgba(1,105,111,0.06)] disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-colors"
                >
                    {computing
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Calculator size={13} strokeWidth={1.5} />}
                    {computing ? "Computing…" : records.length ? "Recompute yield" : "Compute yield"}
                </button>
            </div>

            {error && (
                <div
                    data-testid={`pour-yield-error-${pour.id}`}
                    className="border-l-2 border-[#a13434] bg-[rgba(161,52,52,0.05)] mx-6 mt-4 px-3 py-2.5 text-[12.5px] text-[#a13434] tc-mono"
                >
                    {error.code === "monitoring_missing"
                        ? "Monitoring data required — upload sensor data on the Thermal tab to enable yield."
                        : error.code === "milestone_not_configured"
                            ? "No milestone matches the monitored estimate. Configure at least one milestone above."
                            : error.code === "yield_settings_missing"
                                ? "Yield settings missing — configure currency and rates first."
                                : error.message || "Could not compute yield."}
                </div>
            )}

            {records.length === 0 && !error && !noMilestones && (
                <div className="px-6 py-5 text-[13px] text-[#5a6669]">
                    No yield records yet. Click <span className="tc-mono">Compute yield</span> to read from this pour&apos;s monitored estimate.
                </div>
            )}

            {records.map((r) => (
                <MilestoneResultRow key={r.id} record={r} symbol={symbol} currency={currency} />
            ))}
        </div>
    );
};

const MilestoneResultRow = ({ record, symbol, currency }) => {
    const delay = (record.delay_hours || 0) > 0;
    return (
        <div
            data-testid={`milestone-result-${record.id}`}
            className="px-6 py-5 border-t border-[#d8dcdd] grid grid-cols-1 lg:grid-cols-12 gap-4"
        >
            <div className="lg:col-span-4">
                <div className="text-[14px] font-medium text-[#0e1416]">
                    {record.milestone_label}{" "}
                    <span className="tc-mono text-[11px] text-[#5a6669]">
                        ({record.required_strength_MPa} MPa)
                    </span>
                </div>
                <div
                    data-testid={`milestone-schedule-${record.id}`}
                    className="mt-2 tc-mono text-[12px]"
                >
                    {delay ? (
                        <>
                            Schedule delay:{" "}
                            <span className="text-[#b3741f] font-medium">
                                {fmtNumber(record.delay_hours)} h
                            </span>
                        </>
                    ) : (
                        <>
                            Schedule reclaimed:{" "}
                            <span className="text-[#01696f] font-medium">
                                {fmtNumber(record.t_delta_hours)} h ({fmtNumber(record.t_delta_days)} days)
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* G1 — Direct vs Proxy split */}
            <div className="lg:col-span-5 flex flex-col gap-1.5" data-testid={`milestone-savings-${record.id}`}>
                <div className="tc-mono text-[12px] text-[#0e1416]">
                    <span className="text-[#5a6669]">Direct:</span>{" "}
                    <span className="font-medium">{symbol} {fmtNumber(record.direct_savings)}</span>{" "}
                    <span className="text-[#8a9498]">
                        ({symbol} {fmtNumber(record.formwork_savings)} formwork + {symbol} {fmtNumber(record.labor_savings)} labor)
                    </span>
                </div>
                <div className="tc-mono text-[11.5px] text-[#5a6669]">
                    <span className="opacity-80">Proxy:</span>{" "}
                    {symbol} {fmtNumber(record.proxy_savings)}{" "}
                    <span className="text-[#8a9498]">(overhead / LD)</span>
                </div>
                <div className="tc-mono text-[13px] text-[#0e1416] mt-0.5">
                    Total: <span className="font-medium">{symbol} {fmtNumber(record.total_savings)}</span>
                </div>
            </div>

            {/* G2 — Provenance */}
            <div className="lg:col-span-3 text-right" data-testid={`milestone-provenance-${record.id}`}>
                <div className="tc-mono text-[10px] text-[#8a9498] leading-[1.55] text-right">
                    based on monitored StrengthEstimate ·{" "}
                    <br className="hidden lg:block" />
                    maturity-verified achievement{" "}
                    <span className="text-[#0e1416]">{fmtNumber(record.maturity_verified_achievement_hours)} h</span>{" "}
                    vs contractual{" "}
                    <span className="text-[#0e1416]">{fmtNumber(record.contractual_wait_duration_hours)} h</span>{" "}
                    · computed{" "}
                    <span className="text-[#0e1416]">{new Date(record.computed_at).toLocaleString()}</span>
                </div>
            </div>
        </div>
    );
};

/* ===================== Section E — critical path SVG ===================== */

const SectionE = ({ pours, records }) => {
    const recordsByPour = pours
        .map((p) => ({ pour: p, records: records.filter((r) => r.pour_id === p.id) }))
        .filter((g) => g.records.length > 0);

    return (
        <section data-testid="yield-section-e-critical-path" className="flex flex-col gap-5">
            <SectionLabel index="E" title="Critical path comparison" />
            <p className="tc-mono text-[11.5px] italic text-[#5a6669] leading-[1.5] max-w-[78ch]">
                Contractual wait vs maturity-verified achievement per milestone.
                Reclaim shaded teal; delay shaded amber.
            </p>

            {recordsByPour.length === 0 ? (
                <p className="tc-mono text-[11.5px] text-[#5a6669] border border-dashed border-[#d8dcdd] bg-[#f6f7f6] px-4 py-3">
                    No timelines yet — compute yield to populate.
                </p>
            ) : (
                recordsByPour.map(({ pour, records: pourRecords }) => (
                    <div
                        key={pour.id}
                        data-testid={`pour-critical-path-${pour.id}`}
                        className="border border-[#d8dcdd] bg-[#ffffff]"
                    >
                        <div className="px-5 py-3 border-b border-[#d8dcdd] flex items-baseline gap-3">
                            <span className="tc-mono text-[10px] tracking-widest uppercase text-[#5a6669]">
                                Pour #{pour.sequence_index}
                            </span>
                            <span className="text-[13px] font-medium tracking-tight text-[#0e1416]">
                                {pour.name || `Pour ${pour.sequence_index}`}
                            </span>
                        </div>
                        {pourRecords.map((r) => (
                            <TimelineRow
                                key={r.id}
                                record={r}
                                pourId={pour.id}
                            />
                        ))}
                    </div>
                ))
            )}
        </section>
    );
};

const TimelineRow = ({ record, pourId }) => {
    const W = 720, H = 80, pad = { l: 50, r: 50, t: 18, b: 22 };
    const contractual = Number(record.contractual_wait_duration_hours);
    const verified = Number(record.maturity_verified_achievement_hours);
    const tDelta = Number(record.t_delta_hours);
    const delay = (record.delay_hours || 0) > 0;
    const xMax = Math.max(contractual, verified) * 1.1 || 1;
    const xS = (h) => pad.l + (h / xMax) * (W - pad.l - pad.r);
    const verifiedColor = delay ? "#b3741f" : "#01696f";
    const gapFill = delay ? "rgba(179,116,31,0.15)" : "rgba(1,105,111,0.15)";
    const x1 = xS(Math.min(contractual, verified));
    const x2 = xS(Math.max(contractual, verified));

    const days = (h) => (h / 24).toLocaleString(undefined, { maximumFractionDigits: 2 });
    const caption = delay
        ? `Schedule delay: ${record.delay_hours.toFixed(1)} h · ${days(record.delay_hours)} days`
        : `Schedule compression: ${tDelta.toFixed(1)} h · ${record.t_delta_days?.toFixed?.(2) ?? days(tDelta)} days`;

    return (
        <div
            data-testid={`pour-${pourId}-critical-path-${record.id}`}
            className="px-5 py-4 border-t border-[#d8dcdd] first:border-t-0"
        >
            <div className="flex items-baseline justify-between mb-1">
                <div className="text-[13px] text-[#0e1416]">
                    <span className="font-medium">{record.milestone_label}</span>{" "}
                    <span className="tc-mono text-[11px] text-[#5a6669]">
                        ({record.required_strength_MPa} MPa)
                    </span>
                </div>
                <div className="tc-mono text-[10.5px] text-[#8a9498]">
                    axis: 0 – {xMax.toFixed(1)} h
                </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="block">
                {/* base axis */}
                <line x1={pad.l} y1={H / 2} x2={W - pad.r} y2={H / 2}
                      stroke="#d8dcdd" strokeWidth="0.8" />
                {/* shaded gap */}
                <rect x={x1} y={H / 2 - 6} width={x2 - x1} height={12} fill={gapFill} />
                {/* contractual marker (slate) */}
                <line x1={xS(contractual)} y1={H / 2 - 18} x2={xS(contractual)} y2={H / 2 + 18}
                      stroke="#5a6669" strokeWidth="1.4" />
                <text x={xS(contractual)} y={H / 2 - 22} textAnchor="middle" fontSize="9"
                      fontFamily="JetBrains Mono" fill="#5a6669">
                    contractual {contractual.toFixed(1)} h
                </text>
                {/* maturity-verified marker (teal/amber) */}
                <line x1={xS(verified)} y1={H / 2 - 14} x2={xS(verified)} y2={H / 2 + 14}
                      stroke={verifiedColor} strokeWidth="1.6" />
                <text x={xS(verified)} y={H / 2 + 28} textAnchor="middle" fontSize="9"
                      fontFamily="JetBrains Mono" fill={verifiedColor}>
                    maturity-verified {verified.toFixed(1)} h
                </text>
                {/* tick at zero */}
                <line x1={pad.l} y1={H / 2 - 4} x2={pad.l} y2={H / 2 + 4} stroke="#5a6669" strokeWidth="0.8" />
                <text x={pad.l} y={H / 2 + 28} textAnchor="middle" fontSize="8" fontFamily="JetBrains Mono" fill="#8a9498">0 h</text>
            </svg>
            <p className={`tc-mono text-[11.5px] mt-1 ${delay ? "text-[#b3741f]" : "text-[#01696f]"}`}>
                {caption}
            </p>
        </div>
    );
};

/* ===================== shared ===================== */

const SectionLabel = ({ index, title }) => (
    <div className="flex items-baseline gap-5">
        <span className="tc-mono text-[10.5px] tracking-widest uppercase text-[#01696f]">{index}</span>
        <h3 className="text-[16.5px] font-medium tracking-tight text-[#0e1416]">{title}</h3>
        <div className="flex-1 h-px bg-[#d8dcdd]" />
    </div>
);
